import * as React from "react";
import { datePickerValueToDateOnly } from "@ibada/shared-types";
import { type Table as TanStackTable } from "@tanstack/react-table";
import { FilterIcon, RefreshCwIcon, XIcon } from "lucide-react";

import { useIsMobile } from "../../../lib/use-mobile";
import { cn } from "../../../lib/utils";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { DatePicker } from "../../ui/date-picker";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { MobileListEmpty } from "../../ui/mobile-sheet";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "../../ui/sheet";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "../../ui/drawer";
import { LookupSelect, Select } from "../../ui/select";
import { DataTableFilterChips } from "./data-table-filter-chips";
import { DataTableMobileFilterSheet } from "./data-table-mobile-filters";
import { useDataTableMobileLabels } from "./data-table-mobile-list";
import { DataTableToolbar, type DataTableAction, type DataTableToolbarActionInput } from "./data-table-toolbar";
import { DataTableViewOptions } from "./data-table-view-options";
import {
  countActiveFilters,
  getFieldDefaultValues,
  getSearchDebounceMs,
  getFieldValueLabel,
  isFieldApplied,
  isFieldVisible,
  parseDateValue,
  parseMultiSelectValue,
  serializeMultiSelectValue,
  type TableFilterConfig,
  type TableFilterFieldConfig,
  type TableFilterSelectOption,
  type TableFilterValues,
} from "./data-table-filter-types";
import { selectNextString } from "../../../lib/select-next-string";

export type DataTableFiltersProps<TData = unknown> = {
  config: TableFilterConfig;
  appliedValues: TableFilterValues;
  onApply: (nextValues: TableFilterValues) => void;
  onReset: () => void;
  onRefresh?: () => Promise<unknown> | void;
  isRefreshing?: boolean;
  table?: TanStackTable<TData>;
  toolbarRight?: React.ReactNode;
  toolbarActions?: DataTableToolbarActionInput[];
  /** @deprecated No longer used — the toolbar now owns a single responsive layout. */
  mobileLayout?: "stacked" | "inline";
  /** @deprecated No longer used — the toolbar now owns a single responsive layout. */
  mobileActionPlacement?: "row" | "inline";
  mobileSearchFullWidth?: boolean;
  actionButtonSize?: React.ComponentProps<typeof Button>["size"];
  className?: string;
  /**
   * Lets the configured table toolbar own responsive composition while this
   * component continues to own filter draft state, the filter panel, and chips.
   */
  renderToolbar?: (slots: {
    filter: React.ReactNode;
    columns: React.ReactNode;
  }) => React.ReactNode;
  compactTrigger?: boolean;
};

function resolveIconButtonSize(size: React.ComponentProps<typeof Button>["size"]) {
  if (size === "xs") return "icon-xs";
  if (size === "sm") return "icon-sm";
  if (size === "lg") return "icon-lg";
  return "icon";
}

function buildDraftValues(fields: TableFilterFieldConfig[], values: TableFilterValues): TableFilterValues {
  const draft: TableFilterValues = {};

  for (const field of fields) {
    const defaults = getFieldDefaultValues(field);
    for (const key of Object.keys(defaults)) {
      draft[key] = values[key] ?? defaults[key];
    }
  }

  return draft;
}

function areFilterValuesEqual(left: TableFilterValues, right: TableFilterValues, fields: TableFilterFieldConfig[]): boolean {
  for (const field of fields) {
    const defaults = getFieldDefaultValues(field);
    for (const key of Object.keys(defaults)) {
      if ((left[key] ?? defaults[key]) !== (right[key] ?? defaults[key])) {
        return false;
      }
    }
  }

  return true;
}

function getValuesSignature(values: TableFilterValues, fields: TableFilterFieldConfig[]): string {
  const entries: string[] = [];

  for (const field of fields) {
    const defaults = getFieldDefaultValues(field);
    for (const key of Object.keys(defaults)) {
      entries.push(`${key}:${values[key] ?? defaults[key] ?? ""}`);
    }
  }

  return entries.join("|");
}

function getVisibleFilterFields(fields: TableFilterFieldConfig[], values: TableFilterValues): TableFilterFieldConfig[] {
  return fields.filter((field) => isFieldVisible(field, values));
}

const MAX_TABLE_SEARCH_PLACEHOLDER_LENGTH = 30;
const STANDARD_TABLE_SEARCH_INPUT_WIDTH = "w-full min-w-0 sm:max-w-96";
const COMPACT_CHOICE_FILTER_MAX_OPTIONS = 8;
function getCompactSearchPlaceholder(placeholder: string | undefined): string {
  const normalized = placeholder?.trim();
  if (!normalized) {
    return "Search...";
  }

  if (normalized.length <= MAX_TABLE_SEARCH_PLACEHOLDER_LENGTH) {
    return normalized;
  }

  if (/^rechercher\b/i.test(normalized)) {
    return "Rechercher...";
  }

  if (/^search\b/i.test(normalized)) {
    return "Search...";
  }

  return normalized;
}

function normalizeFilterValuesForVisibility(
  values: TableFilterValues,
  fields: TableFilterFieldConfig[],
): TableFilterValues {
  let nextValues = { ...values };
  let changed = true;

  while (changed) {
    changed = false;

    for (const field of fields) {
      if (isFieldVisible(field, nextValues)) {
        continue;
      }

      const defaults = getFieldDefaultValues(field);
      for (const key of Object.keys(defaults)) {
        const nextValue = defaults[key] ?? "";
        if ((nextValues[key] ?? "") !== nextValue) {
          nextValues = { ...nextValues, [key]: nextValue };
          changed = true;
        }
      }
    }
  }

  return nextValues;
}

function shouldRenderChoicePills(options: TableFilterSelectOption[]) {
  return options.length > 1 && options.length <= COMPACT_CHOICE_FILTER_MAX_OPTIONS;
}

function renderChoicePills({
  fieldId,
  options,
  value,
  disabled,
  testId,
  onChange,
}: {
  fieldId: string;
  options: TableFilterSelectOption[];
  value: string;
  disabled: boolean;
  testId?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className='flex flex-wrap gap-1' role='radiogroup' aria-labelledby={`${fieldId}-label`}>
      {options.map((option) => {
        const optionValue = option.value;
        const selected = value === optionValue || (value.trim() === "" && optionValue.trim() === "");

        return (
          <button
            key={optionValue || "__empty__"}
            type='button'
            role='radio'
            aria-checked={selected}
            disabled={disabled}
            data-testid={testId ? `${testId}-option-${(optionValue || "empty").toLowerCase()}` : undefined}
            className={cn(
              "inline-flex min-h-6 items-center rounded-full border px-2 text-xs font-medium transition-colors",
              "focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px] focus-visible:outline-none",
              "disabled:pointer-events-none disabled:opacity-50",
              selected
                ? "border-primary/35 bg-primary/10 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]"
                : "border-border/70 bg-background text-muted-foreground hover:border-border hover:bg-muted/60 hover:text-foreground",
            )}
            onClick={() => onChange(optionValue)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function renderFilterField(
  field: TableFilterFieldConfig,
  values: TableFilterValues,
  onChange: (key: string, value: string) => void,
) {
  const disabled = field.disabled === true;

  if (field.type === "dateRange") {
    const fromDate = parseDateValue(values[field.fromKey]);
    const toDate = parseDateValue(values[field.toKey]);

    return (
      <div key={field.id} className='rounded-lg border border-border/70 bg-muted/20 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]'>
        <div className='mb-1 space-y-0.5'>
          <Label className='text-[0.8rem] font-semibold text-foreground'>{field.label}</Label>
          {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
        </div>
        <div className='grid gap-1 sm:grid-cols-2'>
          <div className='space-y-1'>
            <Label htmlFor={`${field.id}-from`} className='text-xs text-muted-foreground'>
              {field.fromLabel ?? "From"}
            </Label>
            <DatePicker
              value={fromDate}
              onChange={(date) => onChange(field.fromKey, datePickerValueToDateOnly(date) ?? "")}
              placeholder={field.fromPlaceholder ?? "Select date"}
              className='w-full justify-start'
              disabled={disabled}
            />
          </div>
          <div className='space-y-1'>
            <Label htmlFor={`${field.id}-to`} className='text-xs text-muted-foreground'>
              {field.toLabel ?? "To"}
            </Label>
            <DatePicker
              value={toDate}
              onChange={(date) => onChange(field.toKey, datePickerValueToDateOnly(date) ?? "")}
              placeholder={field.toPlaceholder ?? "Select date"}
              className='w-full justify-start'
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    );
  }

  if (field.type === "numberRange") {
    return (
      <div key={field.id} className='rounded-lg border border-border/70 bg-muted/20 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]'>
        <div className='mb-1 space-y-0.5'>
          <Label className='text-[0.8rem] font-semibold text-foreground'>{field.label}</Label>
          {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
        </div>
        <div className='grid gap-1 sm:grid-cols-2'>
          <div className='space-y-1'>
            <Label htmlFor={`${field.id}-min`} className='text-xs text-muted-foreground'>
              {field.minLabel ?? "Min"}
            </Label>
            <Input
              id={`${field.id}-min`}
              type='number'
              inputMode='decimal'
              value={values[field.minKey] ?? ""}
              placeholder={field.minPlaceholder ?? "Enter minimum"}
              onChange={(event) => onChange(field.minKey, event.target.value)}
              disabled={disabled}
            />
          </div>
          <div className='space-y-1'>
            <Label htmlFor={`${field.id}-max`} className='text-xs text-muted-foreground'>
              {field.maxLabel ?? "Max"}
            </Label>
            <Input
              id={`${field.id}-max`}
              type='number'
              inputMode='decimal'
              value={values[field.maxKey] ?? ""}
              placeholder={field.maxPlaceholder ?? "Enter maximum"}
              onChange={(event) => onChange(field.maxKey, event.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    );
  }

  if (field.type === "multiSelect") {
    const selectedValueList = parseMultiSelectValue(values[field.key] ?? "");
    const selectedValues = new Set(selectedValueList);
    const optionOrder = new Map(field.options.map((option, index) => [option.value, index]));

    if (field.creatable) {
      return (
        <div key={field.id} className='space-y-1'>
          <Label htmlFor={field.id} className='text-[0.8rem] font-semibold text-foreground'>
            {field.label}
          </Label>
          {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
          <Select
            value={selectedValueList}
            onValueChange={(nextValues) => onChange(field.key, serializeMultiSelectValue(nextValues))}
            id={field.id}
            placeholder={field.placeholder ?? "Select values"}
            options={field.options.map((option) => ({
              value: option.value,
              label: option.label,
              testId: field.testId ? `${field.testId}-option-${option.value.toLowerCase()}` : undefined,
            }))}
            isMulti
            isClearable
            creatable
            formatCreateLabel={field.formatCreateLabel}
            disabled={disabled}
          />
        </div>
      );
    }

    return (
      <div key={field.id} className='space-y-1'>
        <Label id={`${field.id}-label`} className='text-[0.8rem] font-semibold text-foreground'>
          {field.label}
        </Label>
        {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
        <div className='divide-y divide-border/40 overflow-hidden rounded-lg border border-border/70 bg-background'>
          {field.options.length === 0 ? (
            <MobileListEmpty className='px-4 py-4 text-left'>
              {field.emptyLabel ?? "No options available."}
            </MobileListEmpty>
          ) : (
            field.options.map((option) => (
              <label key={option.value} className='flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm'>
                <Checkbox
                  checked={selectedValues.has(option.value)}
                  disabled={disabled}
                  onCheckedChange={(checked) => {
                    const next = new Set(selectedValues);
                    if (checked) {
                      next.add(option.value);
                    } else {
                      next.delete(option.value);
                    }

                    const orderedValues = Array.from(next).sort(
                      (a, b) => (optionOrder.get(a) ?? 0) - (optionOrder.get(b) ?? 0),
                    );

                    onChange(field.key, serializeMultiSelectValue(orderedValues));
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))
          )}
        </div>
      </div>
    );
  }

  if (field.type === "select") {
    const emptyValue = "__dt_empty__";
    const selectValue = (values[field.key] ?? "").trim() === "" ? emptyValue : values[field.key] ?? "";
    const pillOptions = field.options.map((option) => ({
      value: option.value,
      label: option.label,
    }));

    if (shouldRenderChoicePills(pillOptions)) {
      return (
        <div key={field.id} className='space-y-1.5'>
          <Label id={`${field.id}-label`} className='text-[0.8rem] font-semibold text-foreground'>
            {field.label}
          </Label>
          {renderChoicePills({
            fieldId: field.id,
            options: pillOptions,
            value: values[field.key] ?? "",
            disabled,
            testId: field.testId,
            onChange: (nextValue) => onChange(field.key, nextValue),
          })}
          {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
        </div>
      );
    }

    return (
      <div key={field.id} className='space-y-1'>
        <Label htmlFor={field.id} className='text-[0.8rem] font-semibold text-foreground'>
          {field.label}
        </Label>
        <Select
          value={selectValue}
          onValueChange={(next) => {
            const nextValue = selectNextString(next);
            const resolvedValue = nextValue === emptyValue ? "" : nextValue;
            onChange(field.key, resolvedValue);
          }}
          id={field.id}
          placeholder={field.placeholder ?? "Select value"}
          options={field.options.map((option) => ({
            value: option.value || emptyValue,
            label: option.label,
            testId: field.testId ? `${field.testId}-option-${(option.value || "empty").toLowerCase()}` : undefined,
          }))}
          creatable={field.creatable}
          formatCreateLabel={field.formatCreateLabel}
          disabled={disabled}
        />
        {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
      </div>
    );
  }

  if (field.type === "lookup" || field.type === "asyncSelect") {
    const emptyValue = "__dt_empty__";
    const selectValue = (values[field.key] ?? "").trim() === "" ? emptyValue : values[field.key] ?? "";

    return (
      <div key={field.id} className='space-y-1'>
        <Label htmlFor={field.id} className='text-[0.8rem] font-semibold text-foreground'>
          {field.label}
        </Label>
        <LookupSelect
          value={selectValue}
          onValueChange={(next) => {
            const nextValue = selectNextString(next);
            const resolvedValue = nextValue === emptyValue ? "" : nextValue;
            onChange(field.key, resolvedValue);
          }}
          id={field.id}
          placeholder={field.placeholder ?? "Select value"}
          options={field.options.map((option) => ({
            value: option.value || emptyValue,
            label: option.label,
            testId: field.testId ? `${field.testId}-option-${(option.value || "empty").toLowerCase()}` : undefined,
          }))}
          searchValue={field.searchValue}
          onSearchValueChange={field.onSearchValueChange}
          searchPlaceholder={field.searchPlaceholder}
          isLoading={field.isLoading}
          loadingMessage={field.loadingMessage}
          mobileEmpty={field.mobileEmpty}
          noOptionsMessage={field.mobileEmpty}
          disabled={disabled}
        />
        {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
      </div>
    );
  }

  if (field.type === "boolean") {
    const emptyValue = "__dt_empty__";
    const selectValue = (values[field.key] ?? "").trim() === "" ? emptyValue : values[field.key] ?? "";
    const pillOptions = [
      { value: "", label: "Any" },
      { value: "true", label: field.trueLabel ?? "True" },
      { value: "false", label: field.falseLabel ?? "False" },
    ];

    return (
      <div key={field.id} className='space-y-1'>
        <Label id={`${field.id}-label`} className='text-[0.8rem] font-semibold text-foreground'>
          {field.label}
        </Label>
        {renderChoicePills({
          fieldId: field.id,
          options: pillOptions,
          value: selectValue === emptyValue ? "" : selectValue,
          disabled,
          testId: field.testId,
          onChange: (nextValue) => onChange(field.key, nextValue),
        })}
        {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
      </div>
    );
  }

  if (field.type === "dateTime") {
    return (
      <div key={field.id} className='space-y-1.5'>
        <Label htmlFor={field.id} className='text-[0.8rem] font-semibold text-foreground'>
          {field.label}
        </Label>
        <DatePicker
          value={parseDateValue(values[field.key])}
          onChange={(date) => onChange(field.key, datePickerValueToDateOnly(date) ?? "")}
          placeholder={field.placeholder ?? "Select date"}
          className='w-full justify-start'
          disabled={disabled}
        />
        {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
      </div>
    );
  }

  return (
    <div key={field.id} className='space-y-1.5'>
      <Label htmlFor={field.id} className='text-[0.8rem] font-semibold text-foreground'>
        {field.label}
      </Label>
      <Input
        id={field.id}
        data-testid={field.testId}
        type='text'
        value={values[field.key] ?? ""}
        placeholder={field.placeholder}
        onChange={(event) => onChange(field.key, event.target.value)}
        disabled={disabled}
      />
      {field.description ? <p className='text-xs text-muted-foreground'>{field.description}</p> : null}
    </div>
  );
}

export function DataTableFilters<TData>({
  config,
  appliedValues,
  onApply,
  onReset,
  onRefresh,
  isRefreshing = false,
  table,
  toolbarRight,
  toolbarActions,
  mobileSearchFullWidth = true,
  actionButtonSize = "default",
  className,
  renderToolbar,
  compactTrigger = false,
}: DataTableFiltersProps<TData>) {
  const isMobile = useIsMobile();
  const mobileLabels = useDataTableMobileLabels();
  const fields = React.useMemo(() => (config.fields ?? []).filter((field) => field.hidden !== true), [config.fields]);
  const hasAdvancedFilters = fields.length > 0;
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [draftValues, setDraftValues] = React.useState<TableFilterValues>(() => buildDraftValues(fields, appliedValues));
  const [isRefreshPending, setIsRefreshPending] = React.useState(false);
  const previousPanelOpenRef = React.useRef(false);
  const appliedValuesSignature = React.useMemo(() => getValuesSignature(appliedValues, fields), [appliedValues, fields]);
  const visibleDraftFields = React.useMemo(() => getVisibleFilterFields(fields, draftValues), [draftValues, fields]);
  const visibleAppliedFields = React.useMemo(() => getVisibleFilterFields(fields, appliedValues), [appliedValues, fields]);

  const search = config.search;
  const hasSearch = Boolean(search);
  const searchDebounceMs = getSearchDebounceMs(search);
  const [searchDraft, setSearchDraft] = React.useState(search?.value ?? "");

  React.useEffect(() => {
    setSearchDraft(search?.value ?? "");
  }, [search?.value]);

  React.useEffect(() => {
    if (!search || searchDebounceMs <= 0) return;

    const timer = window.setTimeout(() => {
      if (searchDraft !== search.value) {
        search.onChange(searchDraft);
      }
    }, searchDebounceMs);

    return () => window.clearTimeout(timer);
  }, [search, searchDebounceMs, searchDraft]);

  React.useEffect(() => {
    const wasOpen = previousPanelOpenRef.current;
    previousPanelOpenRef.current = panelOpen;

    if (!panelOpen) {
      return;
    }

    if (!wasOpen) {
      setDraftValues(buildDraftValues(fields, appliedValues));
      return;
    }

    setDraftValues((currentDraft) =>
      areFilterValuesEqual(currentDraft, appliedValues, fields)
        ? buildDraftValues(fields, appliedValues)
        : currentDraft,
    );
  }, [appliedValues, appliedValuesSignature, fields, panelOpen]);

  const activeFilterCount = React.useMemo(
    () => countActiveFilters(visibleAppliedFields, appliedValues),
    [appliedValues, visibleAppliedFields],
  );

  const appliedFields = React.useMemo(
    () => visibleAppliedFields.filter((field) => isFieldApplied(field, appliedValues)),
    [appliedValues, visibleAppliedFields],
  );

  const clearAppliedField = React.useCallback(
    (field: TableFilterFieldConfig) => {
      const defaults = getFieldDefaultValues(field);
      const nextValues = normalizeFilterValuesForVisibility({ ...appliedValues, ...defaults }, fields);
      onApply(nextValues);
    },
    [appliedValues, fields, onApply],
  );

  const searchNode =
    hasSearch && search ? (
      <Input
        data-testid={search.testId}
        value={searchDraft}
        placeholder={getCompactSearchPlaceholder(search.placeholder)}
        className={cn(
          search.className,
          mobileSearchFullWidth ? "w-full max-w-none" : undefined,
          STANDARD_TABLE_SEARCH_INPUT_WIDTH,
        )}
        onChange={(event) => {
          const nextValue = event.target.value;
          setSearchDraft(nextValue);
          if (searchDebounceMs <= 0) {
            search.onChange(nextValue);
          }
        }}
      />
    ) : null;

  const columnsNode = table ? (
    <DataTableViewOptions
      table={table}
      buttonSize={actionButtonSize}
      className='w-full justify-between sm:w-auto'
    />
  ) : null;
  const inlineColumnsNode = isMobile ? null : columnsNode;

  const refreshLoading = isRefreshing || isRefreshPending;
  const handleRefresh = React.useCallback(async () => {
    if (!onRefresh || refreshLoading) {
      return;
    }

    const result = onRefresh();
    if (!result || typeof (result as PromiseLike<unknown>).then !== "function") {
      return;
    }

    setIsRefreshPending(true);
    try {
      await result;
    } finally {
      setIsRefreshPending(false);
    }
  }, [onRefresh, refreshLoading]);

  // Refresh is a secondary action: inline (icon+label) on desktop, folded into the ⋯ overflow on mobile.
  const refreshAction: DataTableAction | null = onRefresh
    ? {
        id: "refresh",
        label: config.refreshLabel ?? "Refresh",
        icon: <RefreshCwIcon className='size-4' />,
        onSelect: () => {
          void handleRefresh();
        },
        emphasis: "secondary",
        loading: refreshLoading,
        variant: "outline",
        order: 910,
        testId: config.refreshButtonTestId,
      }
    : null;

  const openButtonLabel = config.buttonLabel ?? "Filters";
  // Icon-only (with a corner count badge) on mobile; icon + label on desktop.
  const filterButton = (
    <Button
      type='button'
      variant={compactTrigger ? "ghost" : "outline"}
      size={isMobile || compactTrigger ? resolveIconButtonSize(actionButtonSize) : actionButtonSize}
      // The phone trigger is a circle: the base button clips its overflow, which cut the count badge off.
      className={cn("relative gap-2", isMobile && "overflow-visible")}
      aria-label={openButtonLabel}
      title={openButtonLabel}
      data-testid={config.addFilterButtonTestId}
    >
      <FilterIcon className='size-4' />
      {!isMobile && !compactTrigger ? <span>{openButtonLabel}</span> : null}
      {activeFilterCount > 0 ? (
        <Badge
          variant='default'
          className={cn(
            "pointer-events-none absolute top-0.5 right-0.5 h-3.5 min-w-3.5 justify-center rounded-full border border-background px-1 text-[9px] leading-none shadow-none",
            isMobile
              ? "top-1.5 right-1.5 h-[1.125rem] min-w-[1.125rem] border-2 border-card text-[10px]"
              : "top-0.5 right-0.5",
          )}
        >
          {activeFilterCount}
        </Badge>
      ) : null}
    </Button>
  );

  const panelBody = (
    <div className='flex min-h-0 flex-1 flex-col overflow-y-auto px-3.5 py-3 [&>*]:shrink-0'>
      <div className='grid gap-3'>
        {visibleDraftFields.map((field) =>
          renderFilterField(field, draftValues, (key, value) => {
            setDraftValues((prev) => normalizeFilterValuesForVisibility({ ...prev, [key]: value }, fields));
          }),
        )}
      </div>
    </div>
  );

  const panelFooter = (
    <div className='flex w-full items-center justify-between gap-3'>
      <Button
        type='button'
        variant='text'
        onClick={() => {
          setDraftValues(buildDraftValues(fields, appliedValues));
          setPanelOpen(false);
        }}
      >
        {config.cancelLabel ?? "Cancel"}
      </Button>
      <div className='flex items-center gap-2'>
        <Button
          type='button'
          variant='outline'
          onClick={() => {
            setDraftValues(buildDraftValues(fields, {}));
          }}
        >
          {config.resetLabel ?? "Reset"}
        </Button>
        <Button
          type='button'
          onClick={() => {
            const normalizedDraftValues = normalizeFilterValuesForVisibility(draftValues, fields);
            if (!areFilterValuesEqual(normalizedDraftValues, appliedValues, fields)) {
              onApply(normalizedDraftValues);
            }
            setPanelOpen(false);
          }}
        >
          {config.applyLabel ?? "Apply Filters"}
        </Button>
      </div>
    </div>
  );

  const applyDraft = () => {
    const normalizedDraftValues = normalizeFilterValuesForVisibility(draftValues, fields);
    if (!areFilterValuesEqual(normalizedDraftValues, appliedValues, fields)) {
      onApply(normalizedDraftValues);
    }
    setPanelOpen(false);
  };
  // Long lists and lookups open a picker inside the sheet: give it a fixed tall height so searching never
  // shrinks it.
  const tallMobileSheet = visibleDraftFields.some(
    (field) =>
      field.type === "lookup" ||
      field.type === "asyncSelect" ||
      field.type === "multiSelect" ||
      field.type === "dateRange" ||
      field.type === "dateTime" ||
      (field.type === "select" && field.options.length > 5),
  );

  // A phone gets the filters in the app's one bottom sheet (the Drawer: drag to dismiss, iOS corners), in the
  // app's form style (grouped rows, pickers inside the sheet); a desktop keeps the side panel.
  const addFilterNode = !hasAdvancedFilters ? null : isMobile ? (
      <Drawer open={panelOpen} onOpenChange={setPanelOpen}>
        <DrawerTrigger asChild>{filterButton}</DrawerTrigger>
        <DrawerContent className={cn("bg-[var(--app-canvas)]", tallMobileSheet ? "h-[88dvh] max-h-[88dvh]" : "max-h-[88dvh]")}>
          <DrawerHeader className='flex-row items-center justify-between gap-3 px-4 pt-2 pb-3 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='min-w-0 flex-1 truncate text-lg font-extrabold'>{config.title ?? mobileLabels.filtersTitle}</DrawerTitle>
            <DrawerDescription className='sr-only'>{config.title ?? mobileLabels.filtersTitle}</DrawerDescription>
          </DrawerHeader>
          <DataTableMobileFilterSheet
            open={panelOpen}
            fields={visibleDraftFields}
            values={draftValues}
            onChange={(key, value) => {
              setDraftValues((prev) => normalizeFilterValuesForVisibility({ ...prev, [key]: value }, fields));
            }}
          />
          <div className='grid grid-cols-2 gap-2.5 border-t border-border/60 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]'>
            <button
              type='button'
              onClick={() => setDraftValues(buildDraftValues(fields, {}))}
              className='h-[3.25rem] rounded-full bg-muted text-base font-extrabold text-foreground active:bg-muted'
            >
              {mobileLabels.filtersReset}
            </button>
            <button
              type='button'
              onClick={applyDraft}
              className='h-[3.25rem] rounded-full bg-primary text-base font-extrabold text-primary-foreground active:brightness-95'
            >
              {mobileLabels.filtersApply}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
  ) : (
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetTrigger asChild>{filterButton}</SheetTrigger>
        <SheetContent
          side='right'
          className='w-full gap-0 p-0 data-[side=right]:sm:w-[26rem] data-[side=right]:sm:max-w-[calc(100vw-2rem)]'
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
        >
          <SheetHeader className='border-b border-border/60 px-3.5 py-2.5'>
            <div className='flex items-start justify-between gap-4 pr-10'>
              <div className='min-w-0 space-y-1'>
                <SheetTitle>{config.title ?? openButtonLabel}</SheetTitle>
                {config.description ? <SheetDescription>{config.description}</SheetDescription> : null}
              </div>
              {activeFilterCount > 0 ? <Badge variant='default'>{activeFilterCount}</Badge> : null}
            </div>
          </SheetHeader>
          {panelBody}
          <SheetFooter className='border-t border-border/60 px-3.5 py-2'>{panelFooter}</SheetFooter>
        </SheetContent>
      </Sheet>
  );

  // One unified action list: caller actions, then the freeform `toolbarRight` (folds into ⋯ on mobile,
  // inline on desktop), then Refresh. The filter trigger and column-visibility menu have dedicated slots.
  const resolvedToolbarActions = React.useMemo<DataTableToolbarActionInput[]>(() => {
    const actions: DataTableToolbarActionInput[] = [...(toolbarActions ?? [])];

    // `toolbarRight` is freeform fallback content; declarative `toolbarActions` take precedence when both exist.
    if (toolbarRight && actions.length === 0) {
      actions.push({ id: "toolbar-right", node: toolbarRight, mobileOrder: 800 });
    }

    if (refreshAction) {
      actions.push(refreshAction);
    }

    return actions;
  }, [refreshAction, toolbarActions, toolbarRight]);

  return (
    <div className={cn("space-y-3", className)}>
      {renderToolbar ? (
        renderToolbar({ filter: addFilterNode, columns: columnsNode })
      ) : (
        <DataTableToolbar
          search={searchNode}
          filter={addFilterNode}
          columns={inlineColumnsNode}
          toolbarActions={resolvedToolbarActions}
          actionButtonSize={actionButtonSize}
        />
      )}

      {appliedFields.length > 0 ? (
        <DataTableFilterChips showClearAction clearLabel={config.clearLabel} onClear={onReset}>
          {appliedFields.map((field) => {
            return (
              <span
                key={field.id}
                className='group inline-flex rounded-md text-left outline-none focus-within:ring-2 focus-within:ring-ring/50'
              >
                <span className='inline-flex min-h-6 items-stretch gap-0 overflow-hidden rounded-md border border-border/70 bg-muted/40 text-xs text-muted-foreground transition-colors group-hover:border-border group-hover:bg-muted/60 dark:bg-muted/30'>
                  <button
                    type='button'
                    className='inline-flex items-stretch gap-0 px-2 py-0'
                    onClick={() => {
                      setPanelOpen(true);
                    }}
                    aria-label={`${field.label}: ${getFieldValueLabel(field, appliedValues)}`}
                  >
                    <span className='flex items-center font-medium'>{field.label}</span>
                    <span aria-hidden='true' className='mx-1.5 my-1 w-px shrink-0 self-stretch bg-border' />
                    <span className='flex items-center font-semibold text-foreground/80'>{getFieldValueLabel(field, appliedValues)}</span>
                  </button>
                  <button
                    type='button'
                    className='flex items-center border-l border-destructive/25 bg-destructive/10 px-1.5 text-destructive transition-colors hover:bg-destructive/20 focus-visible:bg-destructive/20 focus-visible:outline-none'
                    onClick={() => clearAppliedField(field)}
                    aria-label={`Clear ${field.label}`}
                  >
                    <XIcon className='size-3' />
                  </button>
                </span>
              </span>
            );
          })}
        </DataTableFilterChips>
      ) : null}
    </div>
  );
}
